import { Axial } from '../game/map/HexCoordinates';

/**
 * Zlecenia i wozy po drucie.
 *
 * **Dwie rzeczy, jedna trasa.** Reguła stojąca przy zakładzie i wóz, który
 * z niej powstał, odpowiadają na jedno pytanie — co dokąd jedzie i czemu —
 * a panel jest jeden, więc i odpowiedź przychodzi jedna.
 */

const API = import.meta.env.VITE_API_URL ?? '';

export interface Shipment {
  id: string;
  /** Budynek, który wysyła. */
  source: string;
  /** Budynek, który przyjmuje — dziś własna osada, jutro cudza. */
  target: string;
  material: string;
  /** Ile ma się uzbierać, zanim wóz wyruszy. */
  atLeast: number;
  /**
   * Ile wóz zabiera. Nieobecne — wszystko, co jest.
   *
   * Dwa różne zdania w dwóch polach: próg mówi **kiedy**, to mówi **ile**.
   */
  amount?: number;
}

export interface ConvoyView extends Axial {
  id: string;
  owner: string;
  /** Ile pól zostało do celu. */
  left: number;
}

export interface Board {
  shipments: Shipment[];
  convoys: ConvoyView[];
}

export interface Refused {
  code: string;
  message: string;
}

async function ask<T>(path: string, token: string, body?: unknown): Promise<T | Refused> {
  const response = await fetch(`${API}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return {} as T;

  const answer = await response
    .json()
    .catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (answer as T) : (answer as Refused);
}

/** Wszystko, co ten gracz ma w regułach i w drodze. */
export function loadBoard(token: string): Promise<Board | Refused> {
  return ask<Board>('/api/world/haulage', token);
}

export function orderShipment(
  token: string,
  order: {
    source: string;
    target: string;
    material: string;
    atLeast: number;
    /** Pominięte albo `null` — wóz zabiera wszystko. */
    amount?: number | null;
  }
): Promise<Shipment | Refused> {
  return ask<Shipment>('/api/world/haulage', token, order);
}

export function cancelShipment(token: string, shipment: string): Promise<unknown | Refused> {
  return ask('/api/world/haulage/cancel', token, { shipment });
}

/** Co jedzie w pobliżu — do narysowania po wejściu do gry. */
export async function loadConvoys(at: Axial, radius: number): Promise<ConvoyView[]> {
  const response = await fetch(
    `${API}/api/world/convoys/at?q=${at.q}&r=${at.r}&radius=${radius}`
  );
  if (!response.ok) throw new Error(`wozy: ${response.status}`);
  return (await response.json()) as ConvoyView[];
}
